<template>
  <div class="chat-assistance-container">
    <div v-if="isLoadingAgentInfo === false" class="chat-header" style="display: none;">
      <div class="flex items-center space-x-4"> 

        <button @click="startNewChat" class="btn btn-sm btn-outline">
          <Icon icon="tabler:plus" width="16" class="mr-1" />
          New Chat
        </button>
      </div>
      <div class="agent-info">
        <span class="agent-name">{{ agentName }}</span>
        <span class="agent-status">Active</span>
      </div>
    </div>
    <div v-if="isLoadingAgentInfo === false" class="chat-content">
      <div class="chat-messages" ref="chatMessages">
        <div v-for="(message, index) in messages" :key="index" 
          :class="['message', message.type === 'user' ? 'user-message' : 'agent-message', message.isLoading === true ? 'typing' : '']" 
          :message-id="message.id" 
          :style="{ direction: message.direction, textAlign: message.direction === 'rtl' ? 'right' : 'left' }">
          <div class="message-wrapper">
            <div class="message-content">
              <template v-if="message.isLoading === true">
                <svg width="60" height="23" viewBox="0 0 120 40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="8"><animateTransform attributeName="transform" type="translate" values="0,0;0,-15;0,0;0,0" keyTimes="0;0.3;0.6;1" calcMode="spline" keySplines="0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1" dur="1.2s" repeatCount="indefinite" begin="0s"/></circle><circle cx="60" cy="20" r="8"><animateTransform attributeName="transform" type="translate" values="0,0;0,-15;0,0;0,0" keyTimes="0;0.3;0.6;1" calcMode="spline" keySplines="0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1" dur="1.2s" repeatCount="indefinite" begin="0.2s"/></circle><circle cx="100" cy="20" r="8"><animateTransform attributeName="transform" type="translate" values="0,0;0,-15;0,0;0,0" keyTimes="0;0.3;0.6;1" calcMode="spline" keySplines="0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1" dur="1.2s" repeatCount="indefinite" begin="0.4s"/></circle></svg>
              </template>
              <template v-else>
                <div v-if="message.type === 'agent'" v-html="sanitizeHtml(message.text)"></div>
                <div v-else>{{ message.text }}</div>
                <div v-if="message.type === 'user' && message.attachedFiles !== undefined && message.attachedFiles !== null && message.attachedFiles.length > 0" class="attached-files-display">
                  <div v-for="(file, fIndex) in message.attachedFiles" :key="fIndex" class="attached-file-item">
                    <template v-if="file.dataUrl !== undefined && file.dataUrl !== null && file.dataUrl !== ''">
                      <img :src="file.dataUrl" :alt="file.name" class="attached-file-preview-image">
                    </template>
                    <template v-else-if="file.type !== undefined && file.type !== null && file.type !== ''">
                      <Icon :icon="getFileIcon(file.type)" class="attached-file-icon" />
                    </template>
                    <template v-else>
                      <Icon :icon="getFileIcon('unknown')" class="attached-file-icon" />
                    </template>
                    <span class="attached-file-name" :title="file.name">{{ file.name }}</span>
                  </div>
                </div>
              </template>
            </div>
             <div v-if="message.type === 'agent' && message.isLoading === false && message.messageId !== undefined && message.messageId !== null" 
                  class="feedback-buttons" 
                  :class="{ 'feedback-provided': feedbackStatus[message.messageId] !== undefined && feedbackStatus[message.messageId] !== null }">
                <button @click="submitFeedback(message.messageId, 'like')" 
                        title="Good response" 
                        :disabled="pendingFeedback[message.messageId] === true && pendingFeedback[message.messageId] !== null">
                  <Icon :icon="feedbackStatus[message.messageId] === 'like' ? 'tabler:thumb-up' : 'tabler:thumb-up'" width="16" />
                </button>
                <button @click="submitFeedback(message.messageId, 'dislike')" 
                        title="Bad response"
                        :disabled="pendingFeedback[message.messageId] === true && pendingFeedback[message.messageId] !== null">
                  <Icon :icon="feedbackStatus[message.messageId] === 'dislike' ? 'tabler:thumb-down' : 'tabler:thumb-down'" width="16" />
                </button>
                <div class="dropdown dropdown-top dropdown-end ml-1">
                  <button tabindex="0" role="button" class="btn btn-xs btn-ghost p-1 normal-case" title="Export response as...">
                    <Icon icon="tabler:download" width="16" /> Export
                  </button>
                  <ul tabindex="0" class="dropdown-content z-[1] menu p-1 shadow bg-base-100 rounded-box w-28 text-xs">
                    <li><a @click="downloadTxt(message)">TXT</a></li>
                    <li><a @click="downloadDocx(message)">DOCX</a></li>
                    <li><a @click="downloadPdf(message)">PDF</a></li>
                  </ul>
                </div>
              </div>
          </div>
        </div>
      </div>
      <div 
        class="chat-input"
        :class="{ 'is-dragging-over': isDragging === true }" 
        @dragenter.prevent.stop="handleDragEnter"
        @dragover.prevent.stop="handleDragOver"
        @dragleave.prevent.stop="handleDragLeave"
        @drop.prevent.stop="handleDrop"
      >
        <div class="staged-files-container" v-if="stagedFiles.length > 0">
          <div v-for="(fileItem, index) in stagedFiles" :key="index" class="file-card">
            <template v-if="fileItem.previewUrl !== null && fileItem.previewUrl !== undefined && fileItem.previewUrl !== ''">
              <img :src="fileItem.previewUrl" class="file-preview-image" :alt="fileItem.file.name" />
            </template>
            <template v-else>
              <Icon :icon="getFileIcon(fileItem.file?.name?.split('.').pop()?.toLowerCase() || 'unknown')" class="file-icon" />
            </template>
            <span class="file-name" :title="fileItem.file.name">{{ fileItem.file.name }}</span>
            <button @click="removeStagedFile(index)" class="remove-file-button">
              <Icon icon="tabler:x" width="14" />
            </button>
          </div>
        </div>
        <div class="input-row">
          <div class="file-upload">
            <input type="file" 
              ref="fileInput" 
              @change="handleFileUpload" 
              multiple 
              class="hidden"
              accept=".pdf,.doc,.docx,.txt,.csv,.json,.md,.png,.jpg,.jpeg,.gif,.bmp,.webp"
            />
            <button @click="triggerFileInput" class="upload-button" title="Attach files">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <textarea v-model="userMessage" 
            @keyup.enter.prevent="sendMessage"
            @input="checkDirection($event.target.value)" 
            :style="{ direction: inputDirection, textAlign: inputDirection === 'rtl' ? 'right' : 'left' }" 
            placeholder="Type your message..."
            ref="messageInput"
          ></textarea>
          <button @click.prevent="sendMessage" class="send-button" title="Send message">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="#fbfcff" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m5 12l-.604-5.437C4.223 5.007 5.825 3.864 7.24 4.535l11.944 5.658c1.525.722 1.525 2.892 0 3.614L7.24 19.466c-1.415.67-3.017-.472-2.844-2.028zm0 0h7"/></svg>
          </button>
        </div>
      </div>
    </div>
    <div v-else class="flex-grow flex items-center justify-center">
      <span class="loading loading-dots loading-lg text-primary"></span>
    </div>
  </div>

  <dialog class="modal" :class="{ 'modal-open': showAccessDeniedModal !== undefined && showAccessDeniedModal !== null && showAccessDeniedModal === true }">
    <div class="modal-box flex flex-col items-center text-center">
      <h3 class="font-bold text-lg text-error flex items-center space-x-2 mb-4">
        <Icon icon="tabler:circle-x" width="48" /> 
        Access Denied!
      </h3>
      <p class="py-4">You do not have permission to access this AI Agent ({{ agentName }}) because you are not assigned to any of its required divisions.</p>
      <div class="modal-action mt-4">
        <button class="btn" @click="router.push('/')">OK</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="router.push('/')">close</button>
    </form>
  </dialog>

  <dialog class="modal" :class="{ 'modal-open': isKbModalOpen }">
    <div class="modal-box w-11/12 max-w-3xl relative"> 
      <button 
          class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2 z-10" 
          @click="isKbModalOpen = false" 
          :disabled="isUploadingKb === true">
        <Icon icon="tabler:x" width="20" />
      </button>
      <h3 class="font-bold text-lg mb-4">Agent Knowledge Base</h3>


      <div v-if="kbUploadError !== null" role="alert" class="alert alert-error mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        <span>Error: {{ kbUploadError }}</span>
        <button class="btn btn-sm btn-ghost" @click="kbUploadError = null">OK</button>
      </div>

  
      <div v-if="isUploadingKb === true || isLoadingKb === true" 
           class="absolute inset-0 bg-base-100 bg-opacity-75 flex flex-col items-center justify-center z-20 rounded-box p-4">
           <span class="loading loading-dots loading-lg text-primary mb-4"></span> 
           <span class="text-lg font-semibold text-center">
             Uploading...
           </span>
           <span class="text-sm text-gray-500 mt-1">Please wait.</span>
      </div>

   
      <div class="mb-6" :class="{ 'opacity-50 pointer-events-none': isUploadingKb === true }">
        <div role="tablist" class="tabs tabs-lifted">
          
          <input type="radio" name="kb_upload_tabs" role="tab" class="tab" aria-label="Upload Files" checked />
          <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-4 border">
            
            <label for="kb-file-upload" class="btn btn-block mb-2" :class="{ 'btn-disabled': isUploadingKb === true }">
              <Icon icon="tabler:upload" width="20" class="mr-2" />
              Select Files to Add
            </label>
            <input 
                id="kb-file-upload"
                type="file" 
                @change="handleKbFileUpload" 
                multiple 
                class="hidden"  
                accept=".pdf,.doc,.docx,.txt,.csv,.json,.md,.png,.jpg,.jpeg,.gif,.bmp,.webp"
                :disabled="isUploadingKb === true" 
            />
          
            <div v-if="kbStagedFiles.length > 0" class="mt-3 space-y-2">
              <h4 class="text-sm font-medium">Files ready to upload:</h4>
              <div v-for="(item, index) in kbStagedFiles" :key="index" class="flex items-center justify-between bg-base-200 p-2 rounded text-xs">
                <span class="truncate" :title="item.file.name">{{ item.file.name }}</span>
                <button class="btn btn-xs btn-circle btn-error btn-ghost" @click="removeKbStagedFile(index)" :disabled="isUploadingKb === true">
              <Icon icon="tabler:x" width="14" style="color: #ef4444;" />
                </button>
              </div>
              <button 
                class="btn btn-sm w-full mt-2" 
                @click="uploadKnowledgeFiles" 
                :disabled="isUploadingKb === true || kbStagedFiles.length === 0">
                  <span v-if="isUploadingKb === true" class="loading loading-spinner loading-xs"></span>
                  {{ isUploadingKb ? 'Uploading...' : 'Upload Staged Files' }}
              </button>
            </div>
            <p v-else class="text-xs text-center text-gray-500 mt-2">Drag & drop or select files to add.</p> 
            
          </div>

          <input type="radio" name="kb_upload_tabs" role="tab" class="tab" aria-label="Add Text" />
          <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-4 border">
            
            <h4 class="text-sm font-medium mb-2">Add Text Snippet:</h4>
            <input 
              type="text"
              v-model="kbTextTitle"
              placeholder="Enter title for text snippet..."
              class="input input-bordered input-sm w-full mb-2"
              :disabled="isUploadingKb === true"
            />
            <textarea 
              v-model="kbTextInput"
              class="textarea textarea-bordered w-full" 
              rows="4" 
              placeholder="Paste or type text content here..."
              :disabled="isUploadingKb === true"
            ></textarea>
            <button 
              class="btn btn-sm w-full mt-2" 
              @click="uploadKnowledgeText" 
              :disabled="isUploadingKb === true || kbTextInput.trim().length === 0">
                <span v-if="isUploadingKb === true" class="loading loading-spinner loading-xs"></span>
                {{ isUploadingKb === true ? 'Adding Text...' : 'Add Text to Knowledge Base' }}
            </button>
            
          </div>
        </div>
      </div>


      <h4 class="font-semibold mb-2" :class="{ 'opacity-50': isUploadingKb === true }">Existing Files:</h4>
      <div class="max-h-80 overflow-y-auto space-y-2" :class="{ 'opacity-50 pointer-events-none': isUploadingKb === true }">
        <div v-if="isLoadingKb === true" class="text-center p-4">
          <span class="loading loading-dots loading-md"></span>
        </div>
        <div v-else-if="knowledgeBaseFiles.length === 0" class="text-center p-4 text-gray-500 italic">
          No knowledge base files found for this agent.
        </div>
        <div v-else v-for="file in knowledgeBaseFiles" :key="file.fileName" class="flex items-center justify-between bg-base-100 p-2 rounded shadow-sm border">
           <div class="flex items-center overflow-hidden mr-2">
             <Icon :icon="getFileIcon(file.fileType)" width="18" class="mr-2 flex-shrink-0"/> 
             <span class="text-sm truncate" :title="file.fileName">{{ file.fileType === 'txt' ? file.fileName.replace(/\.txt$/, '') : file.fileName }}</span>
             <span class="ml-2 text-xs text-gray-400">({{ file.fileType }})</span>
           </div>
            <button class="btn btn-xs btn-error btn-outline" @click="removeKnowledgeFile(file.fileName)" :disabled="isUploadingKb === true">
             <Icon icon="tabler:trash" width="14" style="color: #ef4444;" />
             Remove
           </button>
        </div>
      </div>
      
      <div class="modal-action mt-6">
        <button class="btn" @click="isKbModalOpen = false" :disabled="isUploadingKb === true">Close</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
       <button @click.prevent="handleBackdropClick">close</button>
    </form>
  </dialog>
</template>

<script setup>
import { ref, onMounted, nextTick, inject, onBeforeUnmount, computed, defineProps, defineEmits, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import { Icon } from "@iconify/vue/dist/offline";
import { useToast } from 'vue-toastification';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas-pro';
import { marked } from 'marked';
import * as XLSX from 'xlsx';

const props = defineProps({
  agentId: {
    type: String,
    required: true
  },
  llmFlowRef: {
    type: Object,
    required: false, 
    default: null
  }
});

const emit = defineEmits(['flow-updated']);

const router = useRouter();
const agentName = ref('Flow Builder Agent');
const userMessage = ref('');
const messages = ref([]);
const chatMessages = ref(null);
const messageInput = ref(null);
const sessionId = ref(null);
const apiEndpoint = globalState.apiEndpoint;
const fileInput = ref(null);
const stagedFiles = ref([]);
const hideSidebar = inject('hideSidebar');
const isDragging = ref(false);
const inputDirection = ref('ltr'); 
const showAccessDeniedModal = ref(false);
const agentData = ref(null);
const toast = useToast();
const isLoadingAgentInfo = ref(false);
const feedbackStatus = ref({});
const pendingFeedback = ref({});

function sanitizeHtml(rawHtml) {
  const input = typeof rawHtml === 'string' ? rawHtml : (rawHtml === null || rawHtml === undefined ? '' : String(rawHtml));
  if (input.trim().length === 0) {
    return '';
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString('<div>' + input + '</div>', 'text/html');
  const container = doc.body.firstChild;
  if (container === undefined || container === null) {
    return '';
  }
  const allowed = new Set(['div', 'span', 'p', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td']);
  function clean(node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      if (allowed.has(tag) === false) {
        const parent = node.parentNode;
        while (node.firstChild !== null) {
          parent.insertBefore(node.firstChild, node);
        }
        parent.removeChild(node);
        return;
      }
      if (tag === 'a') {
        const href = node.getAttribute('href') !== null ? node.getAttribute('href') : '';
        if (/^(https?:|mailto:|tel:|#)/i.test(href) === false) {
          node.removeAttribute('href');
        } else {
          node.setAttribute('target', '_blank');
          node.setAttribute('rel', 'noopener noreferrer');
        }
      }
      if (node.hasAttribute('style') === true) {
        node.removeAttribute('style');
      }
      Array.from(node.attributes).forEach(function(attr) {
        if (/^on/i.test(attr.name) === true) {
          node.removeAttribute(attr.name);
        }
      });
    }
    let child = node.firstChild;
    while (child !== null) {
      const next = child.nextSibling;
      clean(child);
      child = next;
    }
  }
  clean(container);
  return container.innerHTML.trim();
}

function markdownToSafeHtml(rawText) {
  const s = typeof rawText === 'string' ? rawText : (rawText === null || rawText === undefined ? '' : String(rawText));
  if (s.trim().length === 0) {
    return '';
  }
  const parsed = marked.parse(s, { gfm: true, breaks: true });
  return sanitizeHtml(parsed);
}

const isKbModalOpen = ref(false);
const knowledgeBaseFiles = ref([]);
const kbStagedFiles = ref([]); 
const isUploadingKb = ref(false);
const isLoadingKb = ref(false);
const kbUploadError = ref(null);
const kbTextInput = ref('');
const kbTextTitle = ref('');

const userPermissions = computed(() => globalState.userInfo?.permissions !== undefined && globalState.userInfo?.permissions !== null ? globalState.userInfo?.permissions : []);
const canModifyAgent = computed(() => {
  const hasPermission = userPermissions.value.includes('modifyAiAgent') === true || userPermissions.value.includes('fullAccessToOrganization') === true;
  console.log('[ChatAgentAssistance] Checking Permissions:', userPermissions.value, 'Result for canModifyAgent:', hasPermission);
  return hasPermission;
});

const getAuthToken = () => {
  const token = document.cookie.split('; ').find(row => row.startsWith('authToken=')!==false)?.split('=')[1];
  if (token === undefined || token === null) {
    return '';
  }
  return token;
};

const getAuthHeaders = (contentType = 'application/json') => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': contentType
  };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const handleAuthError = (error) => {
  if (error.response?.status === 401 || error.response?.status === 403) {
    router.push('/login');
  }
};

onMounted(async () => {
  console.log('[FlowBuilderAgent] Component Mounted.');
  console.log('[FlowBuilderAgent] onMounted - Initial props.llmFlowRef:', props.llmFlowRef);
  console.log('[FlowBuilderAgent] onMounted - Initial props.llmFlowRef?.value:', props.llmFlowRef?.value);

  hideSidebar.value = true;
  const agentId = props.agentId;
  try {
    agentName.value = 'Flow AI Agent';
    
    await createNewSession(agentId); 

  } catch (error) {
    console.error("[FlowBuilderAgent] Error during component mount:", error);
    appendMessage('agent', 'Error initializing Flow Builder Agent. Please try refreshing.');
    handleAuthError(error);
  } finally {
    isLoadingAgentInfo.value = false;
  }
});

watch(() => props.llmFlowRef, (newRef, oldRef) => {
  console.log('[FlowBuilderAgent] Watch triggered for props.llmFlowRef.');
  console.log('  New Ref:', newRef);
  console.log('  New Ref Value:', newRef?.value);
  console.log('  Old Ref:', oldRef);
}, { immediate: true, deep: true });

onBeforeUnmount(() => {
  stagedFiles.value.forEach(fileItem => {
    if (fileItem.previewUrl !== null) {
      URL.revokeObjectURL(fileItem.previewUrl);
    }
  });
});

async function createNewSession(agentId) {
  try {
    const response = await axios.post(`${apiEndpoint}/api/flowAgent/startFlowChat`, {
      agentId: agentId
    }, {
      headers: getAuthHeaders()
    });
    if (response.data !== null && response.data.sessionId !== null && response.data.sessionId.length > 0) {
      sessionId.value = response.data.sessionId;
      const startingMessage = "Hello! How can I help you build or modify the agent's flow today? You can ask me to create steps, modify existing ones, or even analyze uploaded documents (like PDFs or DOCX) to generate flow sections.";
      appendMessage('agent', startingMessage);
      
    } else {
       throw new Error('Failed to get session ID from backend.');
    }
  } catch (error) {
    console.error('[FlowBuilderAgent] Error creating session:', error);
    handleAuthError(error);
    appendMessage('agent', 'Sorry, there was an error starting the flow builder chat. Please try again.');
  }
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const fileType = file.name.split('.').pop()?.toLowerCase() !== undefined && file.name.split('.').pop()?.toLowerCase() !== null ? file.name.split('.').pop()?.toLowerCase() : 'unknown';
      const isImage = file.type !== undefined && file.type !== null && file.type.startsWith('image/') === true;
      resolve({ dataUrl, fileType, isImage });
    };
    reader.onerror = error => reject(error);
    reader.readAsDataURL(file);
  });
}

function getTextDirection(text) {
  if (text === null || text.length < 1) {
    return 'ltr'; 
  }
  const firstChar = text.charCodeAt(0);

  if ((firstChar >= 0x0590 && firstChar <= 0x05FF) || (firstChar >= 0x0600 && firstChar <= 0x06FF)) {
    return 'rtl';
  } else {
    return 'ltr';
  }
}

function parseHtmlDocument(html) {
  const raw = typeof html === 'string' ? html : '';
  if (raw.length === 0) {
    return null;
  }
  if (typeof DOMParser === 'undefined') {
    return null;
  }
  try {
    return new DOMParser().parseFromString(raw, 'text/html');
  } catch (e) {
    return null;
  }
}

function htmlToText(html) {
  const doc = parseHtmlDocument(html);
  if (doc !== null && doc.body !== undefined && doc.body !== null) {
    const tc = doc.body.textContent !== undefined && doc.body.textContent !== null ? doc.body.textContent : (doc.body.innerText !== undefined && doc.body.innerText !== null ? doc.body.innerText : '');
    return typeof tc === 'string' ? tc : '';
  }
  const raw = typeof html === 'string' ? html : '';
  return raw.replace(/<[^>]*>/g, ' ');
}

function checkDirection(text) {
  inputDirection.value = getTextDirection(text);
}

async function sendMessage() {
  console.log('[FlowBuilderAgent] sendMessage function called');
  if ((userMessage.value.trim().length === 0) && stagedFiles.value.length === 0) return;
  
  const messageText = userMessage.value;
  userMessage.value = ''; 
  checkDirection('');

  let filesPayload = [];
  let filesForDisplay = [];

  if (stagedFiles.value.length > 0) {
    const currentStagedFiles = [...stagedFiles.value];
    try {
      const fileReadPromises = currentStagedFiles.map(fileItem => 
        readFileAsBase64(fileItem.file)
      );
      const fileReadResults = await Promise.all(fileReadPromises);

      filesPayload = fileReadResults.map((result, index) => ({
        fileName: currentStagedFiles[index].file.name,
        fileType: result.fileType,
        fileBase64Content: result.dataUrl.split(',')[1]
      }));

      filesForDisplay = fileReadResults.map((result, index) => ({
        name: currentStagedFiles[index].file.name,
        type: result.fileType,
        dataUrl: result.isImage === true ? result.dataUrl : null 
      }));

      for (let i = 0; i < currentStagedFiles.length; i = i + 1) {
        if (currentStagedFiles[i].previewUrl !== null) {
          URL.revokeObjectURL(currentStagedFiles[i].previewUrl);
        }
      }

      stagedFiles.value = [];

    } catch (error) {
      console.error("[FlowBuilderAgent] Error reading files:", error);
      appendMessage('agent', 'Error processing files. Please try again.');
      stagedFiles.value = [];
      return; 
    }
  }
  
  appendMessage('user', messageText, filesForDisplay); 
  
  const agentMessageId = `agent-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  appendMessage('agent', '', null, agentMessageId, true);
  
  await nextTick();
  scrollToBottom();

  let currentFlow = null;
  if (props.llmFlowRef?.value !== null && props.llmFlowRef?.value !== undefined) {
    try {
      currentFlow = props.llmFlowRef.value.getFlowData();
      console.log('[FlowBuilderAgent] Retrieved current flow:', currentFlow);

      if (currentFlow === null || Array.isArray(currentFlow) === false || currentFlow.length === 0) {
          console.error('[FlowBuilderAgent] Retrieved invalid flow data:', currentFlow);
          appendMessage('agent', 'Error: Could not retrieve a valid flow state from the editor. Please ensure the flow is loaded correctly.');
          updateAgentMessage(agentMessageId, 'Internal error: Invalid flow data.'); 
          return;
      }

    } catch (error) {
      console.error('[FlowBuilderAgent] Error getting flow data from ref:', error);
      appendMessage('agent', 'Error retrieving current flow state. Cannot proceed with modification.');
      updateAgentMessage(agentMessageId, 'Internal error: Could not get flow data.');
      return;
    }
  } else {
    console.warn('[FlowBuilderAgent] llmFlowRef is not available.');
    appendMessage('agent', 'Error: Could not access the flow editor component. Please try reloading.');
    updateAgentMessage(agentMessageId, 'Internal error: Flow editor reference missing.'); 
    return;
  }


  try {
    const requestBody = {
      sessionId: sessionId.value,
      question: messageText,
      currentFlow: currentFlow 
    };

    if (filesPayload.length > 0) {
      requestBody.files = filesPayload; 
    }

    const response = await axios.post(`${apiEndpoint}/api/flowAgent/processFlowQuery`, requestBody, {
      headers: getAuthHeaders()
    });
    
    if (response.data !== null) {
        console.log('[FlowBuilderAgent] Received raw response:', response.data);

  
        let agentAnswer = 'Sorry, I could not process the response format.';
        let flowUpdateData = null;

        const answerMatch = response.data.answer?.match(/<answer>([\s\S]*?)<\/answer>/);
        if (answerMatch?.[1] !== undefined) {
            agentAnswer = answerMatch[1].trim();
        } else {
         
             if (typeof response.data.answer === 'string' && response.data.answer.includes('<flowUpdate>') === false) {
                agentAnswer = response.data.answer;
             }
             console.warn('[FlowBuilderAgent] Could not find <answer> tag in response.');
        }

        const flowUpdateMatch = response.data.answer?.match(/<flowUpdate>([\s\S]*?)<\/flowUpdate>/);
        if (flowUpdateMatch?.[1] !== undefined) {
            const flowJsonString = flowUpdateMatch[1].trim();
            try {
                flowUpdateData = JSON.parse(flowJsonString);
                console.log('[FlowBuilderAgent] Parsed flow update data:', flowUpdateData);
            } catch (parseError) {
                console.error('[FlowBuilderAgent] Error parsing flow update JSON:', parseError);
                console.error('[FlowBuilderAgent] Invalid JSON string:', flowJsonString);
                agentAnswer += "\\n\\n(Note: There was an error processing the flow update received from the backend.)";
            }
        }
  

        updateAgentMessage(agentMessageId, agentAnswer, response.data.messageId);

        if (flowUpdateData !== null) {
            console.log('[FlowBuilderAgent] Emitting flow-updated event.');
            emit('flow-updated', flowUpdateData);
        } else if (response.data.flowChanged === true) {
             console.warn("[FlowBuilderAgent] Backend indicated flowChanged=true but no valid <flowUpdate> tag was found or parsed.");
             
        }

    } else {
        updateAgentMessage(agentMessageId, 'Sorry, I received an empty response.');
    }
  } catch (error) {
    console.error('[FlowBuilderAgent] Error sending message:', error);
    handleAuthError(error);
    updateAgentMessage(agentMessageId, 'Sorry, there was an error processing your request. Please try again.');
  }
}

function appendMessage(type, text, files = null, id = null, displayLoadingEffect = false, calculatedDirection = 'ltr') {
  const safeText = (type === 'agent' && displayLoadingEffect === false) ? markdownToSafeHtml(text) : (typeof text === 'string' ? text : (text === null || text === undefined ? '' : String(text)));
  const messageData = { 
    type, 
    text: safeText, 
    id, 
    isLoading: displayLoadingEffect === true, 
    attachedFiles: files,
    direction: displayLoadingEffect === true ? 'ltr' : calculatedDirection 
  };
  messages.value.push(messageData);
  nextTick(() => {
    scrollToBottom();
  });
}

function updateAgentMessage(id, newText, messageId) {
  console.log(`[ChatAgentAssistance] updateAgentMessage called with messageId: ${messageId}`);
  const messageIndex = messages.value.findIndex(msg => msg.id === id && msg.type === 'agent');
  if (messageIndex > -1) {

    const rawText = newText !== undefined && newText !== null && newText !== '' ? newText : '';
    const parsedHtml = markdownToSafeHtml(rawText);
    const direction = getTextDirection(htmlToText(parsedHtml));

    
    messages.value[messageIndex].text = parsedHtml; 
    messages.value[messageIndex].isLoading = false;
    messages.value[messageIndex].messageId = messageId;
    console.log('[ChatAgentAssistance] Agent message object after ID assignment:', messages.value[messageIndex]);
    messages.value[messageIndex].direction = direction; 
    messages.value[messageIndex].domId = `message-content-${messageId || id}`;
    nextTick(() => {
      scrollToBottom();
 
      const updatedMessageElement = chatMessages.value?.querySelector(`.message[message-id="${id}"] .message-content`);
    });
  }
}

function scrollToBottom() {
  if (chatMessages.value !== null) {
    chatMessages.value.scrollTop = chatMessages.value.scrollHeight;
  }
}

function triggerFileInput() {
  fileInput.value.click();
}

function handleDragEnter(event) {
  isDragging.value = true;
}

function handleDragOver(event) {

}

function handleDragLeave(event) {

  if (event.currentTarget.contains(event.relatedTarget) === false) {
       isDragging.value = false;
  }
}

function handleDrop(event) {
  isDragging.value = false;
  const files = event.dataTransfer?.files;
  if (files === undefined || files.length <= 0) return;
  processFiles(files); 
}

function processFiles(files) {
  const newFiles = Array.from(files)
    .filter(file => file.type !== "") 
    .map(file => {
      let previewUrl = null;
      if (file.type.startsWith('image/') === true) {
        previewUrl = URL.createObjectURL(file);
      }
      return { file, previewUrl };
    });


  const uniqueNewFiles = newFiles.filter(newFile => 
    stagedFiles.value.some(existingFile => 
      existingFile.file.name === newFile.file.name && existingFile.file.size === newFile.file.size
    ) === false
  );

  stagedFiles.value = [...stagedFiles.value, ...uniqueNewFiles];
}

function handleFileUpload(event) {
  const files = event.target.files;
  if (files === null || files.length <= 0) return;
  
  processFiles(files); 

  event.target.value = ''; 
}

function removeStagedFile(index) {
  const fileItem = stagedFiles.value[index];
  if (fileItem !== undefined && fileItem.previewUrl !== null) {
    URL.revokeObjectURL(fileItem.previewUrl);
  }
  stagedFiles.value.splice(index, 1);
}

function getFileIcon(fileType) {
  const extension = fileType?.toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(extension) === true) {
    return null;
  }
  switch (extension) {
    case 'pdf': return 'tabler:file-type-pdf';
    case 'doc':
    case 'docx': return 'tabler:file-type-doc';
    case 'txt': return 'tabler:file-text';
    case 'csv': return 'tabler:file-type-csv';
    case 'json': return 'tabler:file-type-json';
    case 'md': return 'tabler:markdown';
    default: return 'tabler:file';
  }
}

function startNewChat() {
  router.go(0);
}

function openKbModal() {
  fetchKnowledgeBase();
  kbStagedFiles.value = [];
  isKbModalOpen.value = true;
}

async function fetchKnowledgeBase() {
  if (isLoadingKb.value === true) return;
  isLoadingKb.value = true;
  knowledgeBaseFiles.value = [];
  const agentId = props.agentId;
  try {
    const response = await axios.get(`${apiEndpoint}/getAgentKnowledgebase/${agentId}`, {
      headers: getAuthHeaders()
    });
    knowledgeBaseFiles.value = response.data || [];
  } catch (error) {
    console.error("Error fetching knowledge base:", error);
    toast.error("Failed to load knowledge base files.");
    handleAuthError(error);
  } finally {
    isLoadingKb.value = false;
  }
}

function handleKbFileUpload(event) {
  const files = event.target.files;
  if (files === null || files.length <= 0) return;
  
  const newFiles = Array.from(files)
    .filter(file => file.type !== "")
    .map(file => ({ file, previewUrl: null }));

  const uniqueNewFiles = newFiles.filter(newFile => 
    kbStagedFiles.value.some(existingFile => 
        existingFile.file.name === newFile.file.name && existingFile.file.size === newFile.file.size
    ) === false
  );
  kbStagedFiles.value = [...kbStagedFiles.value, ...uniqueNewFiles];
  event.target.value = '';
}

function removeKbStagedFile(index) {
  kbStagedFiles.value.splice(index, 1);
}

async function uploadKnowledgeFiles() {
  if (kbStagedFiles.value.length === 0 || isUploadingKb.value === true) return;
  kbUploadError.value = null;
  isUploadingKb.value = true;
  const agentId = props.agentId;

  let filesPayload = [];
  try {
    const fileReadPromises = kbStagedFiles.value.map(item => readFileAsBase64(item.file));
    const fileReadResults = await Promise.all(fileReadPromises);
    
    filesPayload = fileReadResults.map((result, index) => ({
        fileName: kbStagedFiles.value[index].file.name,
        fileType: result.fileType,
        fileData: result.dataUrl.split(',')[1],
        status: 'new'
      }));

  } catch (error) {
      console.error("Error reading files for KB upload:", error);
      toast.error("Error preparing files for upload.");
      isUploadingKb.value = false;
      return;
  }

  try {
    const response = await axios.put(`${apiEndpoint}/addAgentKnowledgebase/${agentId}`, 
      { files: filesPayload }, 
      { headers: getAuthHeaders() } 
    );
    toast.success(response.data.message || "Files added successfully!");
    kbStagedFiles.value = [];
    await fetchKnowledgeBase();
  } catch (error) {
    console.error("Error uploading knowledge base files:", error);
    let errorMessage = "Failed to upload files.";
    if (error.response?.status === 400) {
        errorMessage = error.response?.data?.error !== undefined ? 
            `Upload failed: ${error.response.data.error}. Please check files and try again.` :
            "Upload failed due to invalid request. Please check files and try again.";
    } else if (error.response?.data?.error !== undefined) {
       errorMessage = error.response.data.error;
    } else {
       errorMessage = "An unexpected error occurred during upload.";
    }
    
    kbUploadError.value = errorMessage;

    handleAuthError(error);
  } finally {
    isUploadingKb.value = false;
  }
}

async function removeKnowledgeFile(fileName) {
  if (confirm(`Are you sure you want to remove the file "${fileName}"?`) === false) {
     return;
  }
  const agentId = props.agentId;
  try {
    const response = await axios.delete(`${apiEndpoint}/removeAgentKnowledgebase/${agentId}?fileName=${encodeURIComponent(fileName)}`, {
      headers: getAuthHeaders()
    });
    toast.success(response.data.message !== undefined && response.data.message !== null && response.data.message !== '' ? response.data.message : 'File removed successfully!');
    await fetchKnowledgeBase();
  } catch (error) {
    console.error("Error removing knowledge base file:", error);
     const errorMessage = error.response?.data?.error !== undefined && error.response?.data?.error !== null && error.response?.data?.error !== '' ? error.response?.data?.error : "Failed to remove file.";
    toast.error(errorMessage);
    handleAuthError(error);
  }
}

function handleBackdropClick() {
  if (isUploadingKb.value === false) {
    isKbModalOpen.value = false;
  }
}

async function uploadKnowledgeText() {
  if (kbTextInput.value.trim().length === 0 || isUploadingKb.value === true) return;

  kbUploadError.value = null;
  isUploadingKb.value = true;
  const agentId = props.agentId;

  let textPayload = [];
  try {
    const textContent = kbTextInput.value.trim();
    const title = kbTextTitle.value.trim();
    const fileName = title.length > 0 
      ? `${title}.txt` 
      : `text-snippet-${Date.now()}.txt`;
    
    const fileData = btoa(unescape(encodeURIComponent(textContent)));

    textPayload = [{
      fileName: fileName,
      fileType: 'txt',
      fileData: fileData,
      status: 'new'
    }];

  } catch (error) {
      console.error("Error encoding text for KB upload:", error);
      toast.error("Error preparing text for upload.");
      kbUploadError.value = "Error encoding text.";
      isUploadingKb.value = false;
      return;
  }

  try {
    const response = await axios.put(`${apiEndpoint}/addAgentKnowledgebase/${agentId}`, 
      { files: textPayload }, 
      { headers: getAuthHeaders() } 
    );
    toast.success(response.data.message !== undefined && response.data.message !== null && response.data.message !== '' ? response.data.message : "Text snippet added successfully!");
    kbTextInput.value = ''; 
    kbTextTitle.value = '';
    await fetchKnowledgeBase(); 
  } catch (error) {
    console.error("Error uploading text snippet:", error);
    let errorMessage = "Failed to upload text snippet.";
    if (error.response?.status === 400) {
        errorMessage = error.response?.data?.error !== undefined ? 
            `Upload failed: ${error.response.data.error}.` :
            "Upload failed due to invalid request.";
    } else if (error.response?.data?.error !== undefined) {
       errorMessage = error.response.data.error;
    } else {
       errorMessage = "An unexpected error occurred during upload.";
    }
    
    kbUploadError.value = errorMessage;
    handleAuthError(error);
  } finally {
    isUploadingKb.value = false;
  }
}

async function submitFeedback(messageId, feedbackType) {
  if (messageId === undefined) {
    console.error('Cannot submit feedback: messageId is missing.');
    return;
  }
  if (pendingFeedback.value[messageId] === true) {
    console.log(`Feedback submission already pending for ${messageId}`);
    return; 
  }

  console.log(`Submitting feedback: ${feedbackType} for message ${messageId}`);
  pendingFeedback.value[messageId] = true;
  
  try {
    await axios.post(`${apiEndpoint}/api/feedback`, 
      { messageId, feedbackType }, 
      { headers: getAuthHeaders() }
    );
    feedbackStatus.value[messageId] = feedbackType;
    toast.success('Feedback submitted!');

  } catch (error) {
    console.error('Error submitting feedback:', error);
    toast.error('Failed to submit feedback.');
    handleAuthError(error);
  } finally {
    pendingFeedback.value[messageId] = false;
  }
}


const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};


async function downloadTxt(message) {
  console.log('Download TXT for message:', message.messageId);
  try {
    const textContent = htmlToText(message.text);

    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
    triggerDownload(blob, `agent_response_${message.messageId !== undefined && message.messageId !== null && message.messageId !== '' ? message.messageId : Date.now()}.txt`);
    toast.success('TXT file download started.');
  } catch (error) {
    console.error('Error generating TXT:', error);
    toast.error('Failed to generate TXT file.');
  }
}

async function downloadDocx(message) {
  console.log('Download DOCX for message:', message.messageId);
  try {
    const docTmp = parseHtmlDocument(message.text);
    const paragraphs = [];

    const nodes = docTmp !== null && docTmp.body !== undefined && docTmp.body !== null ? Array.from(docTmp.body.childNodes) : [];

    nodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        paragraphs.push(new Paragraph({ children: [new TextRun(node.textContent)] }));
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.tagName === 'P') {
           paragraphs.push(new Paragraph({ children: [new TextRun(node.textContent)] }));
        } else if (node.tagName === 'BR') {
           paragraphs.push(new Paragraph("")); 
        } else {
     
          paragraphs.push(new Paragraph({ children: [new TextRun(node.textContent)] }));
        }
     
      }
    });

    if (paragraphs.length === 0) {
      const tc = htmlToText(message.text);
      if (tc.trim().length > 0) {
        paragraphs.push(new Paragraph({ children: [new TextRun(tc)] }));
      } else {
        paragraphs.push(new Paragraph({ children: [new TextRun('')] }));
      }
    }

    const doc = new Document({
      sections: [{
        properties: {},
        children: paragraphs,
      }],
    });

    const blob = await Packer.toBlob(doc);
    triggerDownload(blob, `agent_response_${message.messageId !== undefined && message.messageId !== null && message.messageId !== '' ? message.messageId : Date.now()}.docx`);
    toast.success('DOCX file download started.');

  } catch (error) {
    console.error('Error generating DOCX:', error);
    toast.error('Failed to generate DOCX file.');
  }
}

async function downloadPdf(message) {
  console.log('Download PDF for message:', message.messageId);
  
  const hiddenDiv = document.createElement('div');
  hiddenDiv.style.position = 'absolute';
  hiddenDiv.style.left = '-9999px';
  hiddenDiv.style.top = '0';
  hiddenDiv.style.width = '750px'; 
  hiddenDiv.style.padding = '25px'; 
  hiddenDiv.style.background = 'white';
  

  const textContent = htmlToText(message.text);

  const preElement = document.createElement('pre');
  preElement.style.whiteSpace = 'pre-wrap';      
  preElement.style.wordWrap = 'break-word';      
  preElement.style.margin = '0';                   
  preElement.style.fontFamily = 'sans-serif';      
  preElement.style.fontSize = '14px';            
  preElement.style.color = '#1f2937';             
  preElement.textContent = textContent;
  

  preElement.dir = message.direction !== undefined && message.direction !== null && message.direction !== '' ? message.direction : 'ltr'; 
  preElement.style.textAlign = message.direction === 'rtl' ? 'right' : 'left';

  hiddenDiv.appendChild(preElement);
  
  document.body.appendChild(hiddenDiv);

  try {

     await new Promise(resolve => setTimeout(resolve, 50)); 


    const canvas = await html2canvas(hiddenDiv, {
      scale: 2, 
      useCORS: true, 
      logging: false, 
      windowWidth: hiddenDiv.scrollWidth,
      windowHeight: hiddenDiv.scrollHeight 
    });
    
    document.body.removeChild(hiddenDiv);

    const imgData = canvas.toDataURL('image/png');
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'pt', 
      format: 'a4' 
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfPageHeight = pdf.internal.pageSize.getHeight(); 

    const ratio = pdfWidth / imgWidth;
    const scaledCanvasHeight = imgHeight * ratio; 
    
    const numPages = Math.ceil(scaledCanvasHeight / pdfPageHeight);

    let yPositionOnImage = 0; 

    for (let i = 0; i < numPages; i = i + 1) {
      if (i > 0) {
        pdf.addPage();
      }
      
      const yPositionOnPdf = -i * pdfPageHeight; 
      
      pdf.addImage(imgData, 'PNG', 0, yPositionOnPdf, pdfWidth, scaledCanvasHeight);
      
    }

    pdf.save(`agent_response_${message.messageId !== undefined && message.messageId !== null && message.messageId !== '' ? message.messageId : Date.now()}.pdf`);
    toast.success('PDF file download started.');

  } catch (error) {
    console.error('Error generating PDF:', error);
    toast.error('Failed to generate PDF file.');
    if (document.body.contains(hiddenDiv) === true) {
        document.body.removeChild(hiddenDiv);
    }
  }
}



function downloadTableAsXlsx(tableElement) {
  if (tableElement === null || tableElement.tagName !== 'TABLE') {
    console.error('Invalid element provided for XLSX download:', tableElement);
    toast.error('Could not find table data to download.');
    return;
  }

  try {

    const ws = XLSX.utils.table_to_sheet(tableElement);
    
 
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');


    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });


    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    triggerDownload(blob, `table_export_${Date.now()}.xlsx`);
    toast.success('XLSX file download started.');

  } catch (error) {
    console.error('Error generating XLSX:', error);
    toast.error('Failed to generate XLSX file.');
  }
}


</script>

<style scoped>
#footer {    display: none;}


#knowledge-base-fab {
  display: none;
}


@media (min-width: 768px) {
  #knowledge-base-fab {

    display: inline-flex; 

    align-items: center;
    justify-content: center;
  }
}

.chat-assistance-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f8fafc;
  margin-left: 0;
  width: 100%;
}

.chat-header {
  padding: 1.5rem;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
}

.agent-info {
  display: flex;
  align-items: center;
  
}
.agent-info > * + * { margin-left: 1rem; }

.agent-name {
  font-weight: 700;
  color: #1e293b;
  font-size: 1.25rem;
}

.agent-status {
  padding: 0.25rem 0.75rem;
  background: #dcfce7;
  color: #166534;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
}
[id='footer']{background:#FFF}
.chat-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: white;
  position: relative;
  width: 100%;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 0.1rem;
  display: flex;
  flex-direction: column;

  overflow-x: hidden;
}

.message {
  max-width: 80%;
  padding: 0.75rem 1rem;
  border-radius: 1rem;
  line-height: 1.5;
  opacity: 0;
  transform: translateY(20px);
  animation: messageAppear 0.3s ease forwards;
}

@keyframes messageAppear {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.user-message {
  align-self: flex-end;
  background: #2563eb;
  color: white;
  border-bottom-right-radius: 0.25rem;
  animation: userMessageAppear 0.3s ease forwards;
}

@keyframes userMessageAppear {
  to {
    opacity: 1;
    transform: translateX(0);
  }
  from {
    opacity: 0;
    transform: translateX(20px);
  }
}

.agent-message {
  align-self: flex-start;
  background: #f1f5f9;
  color: #1e293b;
  border-bottom-left-radius: 0.25rem;
  animation: agentMessageAppear 0.3s ease forwards;
}

@keyframes agentMessageAppear {
  to {
    opacity: 1;
    transform: translateX(0);
  }
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
}

.chat-input {
  position: relative;
  width: 100%;
  max-width: 100%;
  background: white;
  border-radius: 0;
  box-shadow: 0 -2px 5px -1px rgba(0, 0, 0, 0.1);
  z-index: 10;
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
  border: 2px dashed transparent;
  transition: border-color 0.2s ease-in-out, background-color 0.2s ease-in-out;
  margin-top: auto;
}
.is-dragging-over {
  border-color: #2563eb;
  background-color: #eff6ff;
}

.staged-files-container {
  display: flex;
  flex-wrap: wrap;
  margin-top: -0.5rem;
  margin-left: -0.5rem;
  padding: 0.2rem 0.5rem 0.6rem;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 0.5rem;
}
.staged-files-container > * { margin-top: 0.5rem; margin-left: 0.5rem; }

.file-card {
  display: flex;
  align-items: center;
  background-color: #eef2ff;
  color: #4338ca;
  padding: 0.25rem 0.5rem;
  border-radius: 0.75rem;
  font-size: 0.8rem;
  max-width: 180px;
  border: 1px solid #c7d2fe;
  overflow: hidden;
}

.file-preview-image {
  width: 24px;
  height: 24px;
  object-fit: cover;
  margin-right: 0.4rem;
  border-radius: 4px;
  flex-shrink: 0;
}

.file-icon {
  margin-right: 0.3rem;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}

.file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-grow: 1;
}

.remove-file-button {
  background: none;
  border: none;
  color: #6366f1;
  cursor: pointer;
  padding: 0 0 0 0.3rem;
  margin-left: 0.2rem;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.remove-file-button:hover {
  color: #4338ca;
}

.input-row {
  display: flex;
  align-items: flex-start;
}
.input-row > * + * { margin-left: 0.5rem; }

.file-upload {
  align-items: center;
}

.upload-button {
  padding: 0.65rem;
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  height: 4.2rem;
}

.upload-button:hover {
  background: #e2e8f0;
  color: #475569;
}

.hidden {
  display: none;
}

textarea {
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  resize: none;
  min-height: 42px;
  max-height: 120px;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.5;
}

textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

.send-button {
  padding: 0.65rem;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  height: 42px;
}

.send-button:hover {
  background: #1d4ed8;
}

.send-button:active {
  background: #1e40af;
}

.message-content {
  white-space: pre-wrap;
  word-wrap: break-word;
}

.message-content :deep(p) {
  margin: 0;
  line-height: 1.5;
}

.message-content :deep(svg) {
    display: block;
    margin: auto;
}

.agent-message.typing .message-content {
 
  min-height: 30px;
  padding: 0.5rem 1rem; 
}


.agent-message.typing svg {
  height: 20px;
  width: auto;
}

.agent-message.typing circle {
  fill: #64748b; 
}

.no-sidebar {
  margin-left: 0;
  width: 100vw;
}

.attached-files-display {
  display: flex;
  flex-wrap: wrap;
  margin-top: -0.5rem;
  margin-left: -0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}
.attached-files-display > * { margin-top: 0.5rem; margin-left: 0.5rem; }

.agent-message .attached-files-display {
   border-top: 1px solid #e2e8f0;
}

.attached-file-item {
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.15);
  color: inherit;
  padding: 0.2rem 0.4rem;
  border-radius: 0.5rem; 
  font-size: 0.75rem;
  max-width: 150px; 
  border: 1px solid rgba(255, 255, 255, 0.3);
  overflow: hidden;
}

.attached-file-preview-image {
  width: 20px; 
  height: 20px; 
  object-fit: cover; 
  margin-right: 0.3rem;
  border-radius: 3px; 
  flex-shrink: 0;
}

.attached-file-icon {
  width: 14px; 
  height: 14px;
  margin-right: 0.3rem;
  flex-shrink: 0;
}

.attached-file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-grow: 1; 
}

.feedback-buttons {
  display: flex;
  
  padding: 0.2rem;
  margin-top: 0.5rem;
}
.feedback-buttons > * + * { margin-left: 0.3rem; }

.feedback-buttons button {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #475569;
  border-radius: 4px;
  transition: background-color 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feedback-buttons button:hover:not(:disabled) {
  background-color: rgba(0, 0, 0, 0.1);
  color: #1e293b;
}

.feedback-buttons button:disabled {
  cursor: default;
  opacity: 0.7;
}

.feedback-buttons.feedback-provided button:not([data-selected="true"]) {

}

.feedback-buttons.feedback-provided button[data-selected="true"] {
   
}

.message-wrapper { 
  
}


.message-content :deep(.markdown-table-wrapper) {
  position: relative;
  margin-top: 1em;
  margin-bottom: 1em;
  overflow-x: auto; 
  padding-top: 0.5em;
}

.message-content :deep(.download-table-btn) {
  position: absolute;
  top: 0px; 
  left: 5px;
  z-index: 5;
  background-color: #e2e8f0; 
  color: #4b5563; 
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 2px 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  opacity: 0.6; 
  white-space: nowrap; 
  width: 80px;
}

.message-content :deep(.markdown-table-wrapper:hover .download-table-btn) {
  opacity: 1; 
}

.message-content :deep(.download-table-btn:hover) {
  background-color: #cbd5e1; 
  color: #1f2937;
}

.message-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1.5em; 
  border-radius: 8px; 
  overflow: hidden; 
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.message-content :deep(th),
.message-content :deep(td) {
  border: 1px solid #e2e8f0; 
  padding: 8px 12px;
  text-align: left;
  color: #1f2937; 
}

.message-content :deep(th) {
  background-color: #f1f5f9; 
  font-weight: 600;
}

.message-content :deep(tr:hover) {
  background-color: #f8fafc; 
}

</style> 